import debug from "../debug";
import debounce from "../debounce";
import {getEventTarget} from "../event";
import {validateField, validateForm, showTooltip, hideTooltip} from "./validation";
import initAutocompleteFieldsInForm, {initAutocompleteField} from "./autocomplete";

/**
 * Show masked text
 *
 * @param e {Event}
 */
const showMaskedText = e => {
  const target = getEventTarget(e);
  target.parentNode.querySelector(".fe__show_masked_text").classList.add("norender");
  target.parentNode.querySelector(".fe__hide_masked_text").classList.remove("norender");
  target.parentNode.querySelector(".fe__masked_text_element").setAttribute("type", "text");
};

/**
 * Hide masked text
 *
 * @param e {Event}
 */
const hideMaskedText = e => {
  const target = getEventTarget(e);
  target.parentNode.querySelector(".fe__hide_masked_text").classList.add("norender");
  target.parentNode.querySelector(".fe__show_masked_text").classList.remove("norender");
  target.parentNode.querySelector(".fe__masked_text_element").setAttribute("type", "password");
};

/**
 * Attach event handlers to a field
 *
 * @param field {HTMLElement}
 * @param options {Object}
 */
const attachEventHandlersToField = (field, options) => {
  debug(["🐛 %cforms ~ attachEventHandlersToField", "font-weight:bold"], "Input event listener attachment for 👉",
    field, "👈", "with options 👉", options, "👈");
  field.addEventListener("input", debounce((e) => {
    const target = getEventTarget(e);
    validateField(target, options);
  }, 100));
  field.addEventListener("focus", debounce(e => {
    showTooltip(getEventTarget(e), null);
  }, 100));
  field.addEventListener("blur", debounce(e => {
    hideTooltip(getEventTarget(e));
  }, 100));

  // Burn cross-session preview fill. note, this will not stop autofill for values known
  // to the browser and appropriate to the autofill type.
  // It will just stop them from being prefilled without any interaction on the part of the user.
  switch (field.nodeName.toLowerCase()) {
    case "input":
      field.value = "";
      break;
  }
};

/**
 * Init form handlers and validators
 *
 * @param form {HTMLFormElement}
 * @param formControl {String|HTMLElement} To enable when form valid
 * @param fieldsValidators {Object} The purpose of this data structure is to associate up to 4 facts with each keyed DOM element
 *                                  (1) re: a regular expression used to validate the field
 *                                  (2) function: to be invoked for fields with validation or state reply requirements which
 *                                      exceed plain regexp validation
 *                                  (3) form: the form id associated with this element
 *                                  (4) valid: a state value altered by forms.js when input is supplied which complies with re
 *                                      and/or function (Default undefined)
 *                                  (5) guidance: text to display in tooltip
 *                                  (6) required: boolean if the field is required
 * @param autocompleteGlobalOpts {Object} Key:Options pair to configure autocomplete fields separated, @see autocomplete initAutocompleteFieldsInForm for more info
 * @param autocompleteFields {Object} Key:Options pair to configure autocomplete fields separated, @see autocomplete initAutocompleteField for more info
 * @param successCallback {Function} To execute when form valid
 */
const initForm = (form, {
  formControl, fieldsValidators = {}, autocompleteGlobalOpts = {}, autocompleteFields = {}, successCallback = null,
}) => {
  for (let fieldName in fieldsValidators) {
    // if the DOM does not comprise a field described
    if (!form.querySelector(`#${fieldName}`)) {
      debug(["🐛 %cforms ~ initForm", "font-weight:bold"],
        `input event listener attachment failed for 👉 #${fieldName} 👈 due missing DOM element in form`, form,
        "kindly inspect your markup");

      // can't attach to a missing field in form
      return;
    }

    // Attach event handlers
    attachEventHandlersToField(form.querySelector(`#${fieldName}`), fieldsValidators[fieldName]);
  }

  // Init autocomplete fields
  for (let autoFieldName in autocompleteFields) {
    let autocompleteField = form.querySelector(`#${autoFieldName}`);
    if (!autocompleteField) {
      debug(["🐛 %cforms ~ initForm", "font-weight:bold"],
        `Autocomplete field 👉 #${autoFieldName} 👈 missing DOM element in form ${form.id}`, form,
        "kindly inspect your markup");
    }
    initAutocompleteField(autocompleteField, autocompleteFields[autoFieldName]);
  }
  initAutocompleteFieldsInForm(form, autocompleteGlobalOpts);

  // Disable form submit
  form.addEventListener("submit", e => {
    e.preventDefault();

    return false;
  });

  // Show masked texts
  form.querySelectorAll(".fe__show_masked_text").forEach(el => {
    el.addEventListener("click", showMaskedText);
  });

  // Hide masked texts
  form.querySelectorAll(".fe__hide_masked_text").forEach(el => {
    el.addEventListener("click", hideMaskedText);
  });

  let formC = formControl;
  if ("string" === typeof (formControl)) {
    formC = form.querySelector(`#${formControl}`);
    if (!formC) {
      debug(["🐛 %cforms ~ initForm", "font-weight:bold"],
        `Attaching click submit event listener attachment failed for 👉 #${formControl} 👈 due missing DOM element in form`,
        form,
        "kindly inspect your markup");

      return;
    }
  }

  // On form control click
  formC.successCallback = successCallback;
  formC.formLinked = form;
  formC.addEventListener("click", debounce(submitForm, 100));

  // Attach for later use
  form.fieldsValidators = fieldsValidators;
  form.formControl = formC;
};

/**
 * Submit a form
 *
 * @param e {Event}
 *
 * @return {Boolean}
 */
const submitForm = e => {
  // Button
  const target = getEventTarget(e);
  // Form
  const form = target.formLinked;

  if (!form || "undefined" === typeof (form.id)) {
    debug(["🐛 %cforms ~ submitForm", "font-weight:bold"],
      "submitForm looked for but did not find the form associated with the submit button.", target, e);
    // Can't submit a form without a form id
    return false;
  }

  if (validateForm(form.id)) {
    if ("function" === typeof (target.successCallback)) {
      // Pass form as this and button as first parameter
      target.successCallback(form, target);
    }

    return true;
  }

  if (!form.fieldsValidators) {
    debug(["🐛 %cforms ~ submitForm", "font-weight:bold"],
      `Unable to get field validators for form with selector form#${form.id}`, form);

    return false;
  }

  for (const fieldName in form.fieldsValidators) {
    if (!form.querySelector(`#${fieldName}`)) {
      debug(["🐛 %cforms ~ submitForm", "font-weight:bold"],
        `Validation on submit failed for 👉 #${fieldName} 👈 due missing DOM element in form`, form,
        "kindly inspect your markup");

      // can't attach to a missing field in form
      return false;
    }

    // Shows tooltip
    validateField(form.querySelector(`#${fieldName}`), form.fieldsValidators[fieldName]);
  }

  return false;
};

export {attachEventHandlersToField, initForm};
export default initForm;
